import type { AriaToastProps } from '@react-aria/toast';
import { useToast } from '@react-aria/toast';
import { ToastState } from '@react-stately/toast';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import Text from '~/components/typographies/Text';
import IconAlert from '~/icons/icon-alert-triangle-fill.svg?react';
import IconCheck from '~/icons/icon-check-round-fill.svg?react';
import IconCloseFill from '~/icons/icon-close-round-fill.svg?react';
import IconInfo from '~/icons/icon-info-round-fill.svg?react';
import IconLoader from '~/icons/icon-loader.svg?react';
import IconQuestionMark from '~/icons/icon-questionmark-round-fill.svg?react';
import { ToastInfo } from '~/types/toast';
import styles from './Toast.module.scss';

interface ToastProps extends AriaToastProps<ToastInfo> {
  state: ToastState<ToastInfo>;
  viewTransitionName: string;
}

const iconMap = {
  success: <IconCheck className={clsx(styles.icon, styles.successIcon)} />,
  error: <IconCloseFill className={clsx(styles.icon, styles.errorIcon)} />,
  warning: <IconAlert className={clsx(styles.icon, styles.warningIcon)} />,
  info: <IconInfo className={clsx(styles.icon, styles.infoIcon)} />,
  neutral: <IconQuestionMark className={styles.icon} />,
  loading: <IconLoader className={clsx(styles.icon, styles.loadingIcon)} />,
};

const classMap = {
  success: [styles.success],
  error: [styles.error],
  warning: [styles.warning],
  info: [styles.info],
  neutral: [styles.default],
  loading: [styles.info],
};

export function Toast({ state, ...props }: ToastProps) {
  const ref = useRef(null);
  const { toastProps, contentProps, titleProps, closeButtonProps: _close } = useToast(props, state, ref);

  useEffect(() => {
    if (!props.toast.timeout) return;
    const id = window.setTimeout(() => state.close(props.toast.key), props.toast.timeout);
    return () => window.clearTimeout(id);
  }, [props.toast.key, props.toast.timeout, state]);

  const content = props.toast.content;
  const icon = iconMap[content.type];

  const computedContainerClassName = clsx(styles.container, classMap[content.type]);

  return (
    <div
      {...toastProps}
      ref={ref}
      className={computedContainerClassName}
      style={{ viewTransitionName: props.viewTransitionName }}
    >
      <div {...contentProps} className={styles.content}>
        {icon}
        {typeof content.message === 'string' ? (
          <div {...titleProps}>
            <Text variant="body2" weight="regular">
              <Trans>{content.message}</Trans>
            </Text>
          </div>
        ) : (
          content.message()
        )}
        {content.link && (
          <Button href={content.link.href} variant="link" target={content.link.target}>
            {content.link.label}
          </Button>
        )}
      </div>
    </div>
  );
}
